import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu, X, Phone, Mail, Instagram,
  TrendingUp, ChevronRight, ArrowRight,
  Building2, Home as HomeIcon, Hotel, MapPin,
  Star, DollarSign, Users,
} from 'lucide-react';
import { IMAGES } from '@/assets/images';
import {
  MACRO_STATS,
  RESTAURANTE_STATS,
  AIRBNB_STATS,
  HOTEL_STATS,
  PROCESS_STEPS,
  CASE_STUDIES,
  GALLERY_IMAGES,
} from '@/lib/index';
import { springPresets } from '@/lib/motion';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const scrollToSection = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

const IMG_FILTER = { filter: 'sepia(5%) contrast(95%)' };

// ─── Section Label ────────────────────────────────────────────────────────────
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs tracking-[0.35em] uppercase font-sans text-muted-foreground mb-6">
      {children}
    </p>
  );
}

// ─── Insight Box ─────────────────────────────────────────────────────────────
function InsightBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="p-5 mt-6"
      style={{
        background: 'oklch(0.92 0.010 46)',
        borderLeft: '2px solid oklch(0.55 0.045 40)',
      }}
    >
      <p
        className="font-sans text-sm leading-relaxed italic"
        style={{ color: 'oklch(0.35 0.020 40)' }}
      >
        {children}
      </p>
    </div>
  );
}

// ─── Navbar ───────────────────────────────────────────────────────────────────
function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const links = [
    { label: 'Restaurantes', id: 'restaurantes' },
    { label: 'Airbnb', id: 'airbnb' },
    { label: 'Hoteles', id: 'hoteles' },
    { label: 'Casos Globales', id: 'casos' },
    { label: 'Proceso', id: 'proceso' },
    { label: 'Contacto', id: 'contacto' },
  ];

  const navBg = scrolled ? 'oklch(0.97 0.008 50 / 0.96)' : 'transparent';
  const navColor = scrolled ? 'oklch(0.18 0.012 40)' : 'white';

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-700"
      style={{
        background: navBg,
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: scrolled ? '1px solid oklch(0.88 0.012 45 / 0.4)' : 'none',
      }}
    >
      <div className="container mx-auto px-6 py-4 flex items-center justify-between">
        <button onClick={() => scrollToSection('hero')} className="cursor-pointer">
          <img
            src={IMAGES.WHATSAPP_IMAGE_2026_04_23_AT_21_58_42_REMOVEBG_PREVIEW_1}
            alt="Etérico Studio"
            className="h-9 w-auto transition-all duration-500"
            style={{ filter: scrolled ? 'none' : 'brightness(0) invert(1)' }}
          />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollToSection(l.id)}
              className="text-xs tracking-widest uppercase font-sans transition-opacity hover:opacity-50"
              style={{ color: navColor }}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => scrollToSection('contacto')}
            className="px-5 py-2 text-xs tracking-widest uppercase font-sans transition-all hover:opacity-75"
            style={{
              background: scrolled ? 'oklch(0.18 0.012 40)' : 'white',
              color: scrolled ? 'white' : 'oklch(0.18 0.012 40)',
            }}
          >
            Diagnóstico gratis
          </button>
        </nav>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          style={{ color: navColor }}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={springPresets.gentle}
            className="lg:hidden bg-background border-t border-border overflow-hidden"
          >
            <div className="container mx-auto px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => { scrollToSection(l.id); setOpen(false); }}
                  className="text-left text-sm tracking-widest uppercase text-foreground hover:text-muted-foreground transition-colors"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section
      id="hero"
      className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: 'url(/images/cdmx_hotel_2.png)' }}
      />
      {/* Gradient overlay — más ligero para dejar respirar la imagen */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to bottom, oklch(0.10 0.010 38 / 0.35) 0%, oklch(0.08 0.010 38 / 0.55) 60%, oklch(0.08 0.010 38 / 0.72) 100%)',
        }}
      />

      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        {/* Eyebrow — marca y subtítulo */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.smooth, delay: 0.15 }}
          className="font-sans text-xs tracking-[0.25em] uppercase mb-6"
          style={{ color: 'oklch(0.82 0.018 50 / 0.85)' }}
        >
          Etérico Studio · Diseño de Interiores Sensorial
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.smooth, delay: 0.3 }}
          className="font-heading text-5xl md:text-7xl lg:text-8xl text-white leading-[1.05] mb-6"
          style={{ fontWeight: 300, letterSpacing: '-0.01em' }}
        >
          Tu negocio en CDMX
          <br />
          <span style={{ fontStyle: 'italic', color: 'oklch(0.88 0.045 58)' }}>
            merece ganar más
          </span>
        </motion.h1>

        {/* Sector badges — debajo del headline para no competir */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.smooth, delay: 0.48 }}
          className="flex flex-wrap justify-center gap-3 mb-8"
        >
          {[
            { icon: Building2,  label: 'Restaurantes' },
            { icon: HomeIcon,   label: 'Airbnb' },
            { icon: Hotel,      label: 'Hoteles Boutique' },
          ].map(({ icon: Icon, label }) => (
            <span
              key={label}
              className="inline-flex items-center gap-2 px-4 py-1.5 text-xs tracking-widest uppercase font-sans"
              style={{
                border: '1px solid oklch(0.85 0.010 50 / 0.35)',
                color: 'oklch(0.88 0.012 50)',
                background: 'oklch(0.08 0.010 38 / 0.30)',
              }}
            >
              <Icon size={12} strokeWidth={1} />
              {label}
            </span>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.smooth, delay: 0.62 }}
          className="font-sans text-base md:text-lg mb-12 max-w-2xl mx-auto leading-relaxed"
          style={{ color: 'oklch(0.87 0.012 50)', fontWeight: 300 }}
        >
          Diseño de interiores que aumenta tu ticket, llena tu hotel y viraliza tu
          Airbnb. Basado en ciencia. Construido en México.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.smooth, delay: 0.65 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <button
            onClick={() => scrollToSection('roi')}
            className="inline-flex items-center justify-center gap-2 px-10 py-4 text-xs tracking-[0.2em] uppercase font-sans transition-all hover:opacity-80"
            style={{ background: 'oklch(0.92 0.010 50)', color: 'oklch(0.18 0.012 40)' }}
          >
            <TrendingUp size={14} strokeWidth={1.5} />
            Ver el ROI
          </button>
          <button
            onClick={() => scrollToSection('transformacion')}
            className="inline-flex items-center justify-center gap-2 px-10 py-4 text-xs tracking-[0.2em] uppercase font-sans transition-all hover:opacity-80"
            style={{
              border: '1px solid oklch(0.85 0.010 50 / 0.5)',
              color: 'oklch(0.90 0.010 50)',
            }}
          >
            Ver transformaciones
            <ArrowRight size={14} strokeWidth={1.5} />
          </button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="w-px h-10"
          style={{ background: 'oklch(0.75 0.015 50 / 0.5)' }}
        />
      </motion.div>
    </section>
  );
}

// ─── Macro ROI Stats ──────────────────────────────────────────────────────────
function MacroStats() {
  return (
    <section id="roi" className="py-20 px-6" style={{ background: 'oklch(0.12 0.015 40)' }}>
      <div className="container mx-auto max-w-5xl">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-xs tracking-[0.4em] uppercase font-sans mb-12"
          style={{ color: 'oklch(0.55 0.025 45)' }}
        >
          El diseño no es un gasto — es tu mayor ventaja competitiva en CDMX
        </motion.p>

        <div className="grid md:grid-cols-3 gap-px bg-muted/20">
          {MACRO_STATS.map((s, i) => (
            <motion.div
              key={s.sector}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springPresets.gentle, delay: i * 0.15 }}
              className="text-center p-12"
              style={{ background: 'oklch(0.15 0.018 38)' }}
            >
              <p
                className="font-sans text-xs tracking-widest uppercase mb-4"
                style={{ color: 'oklch(0.55 0.025 45)' }}
              >
                {s.sector}
              </p>
              <p
                className="font-heading text-6xl md:text-7xl mb-2"
                style={{ fontWeight: 300, color: 'oklch(0.68 0.080 60)' }}
              >
                {s.headline}
              </p>
              <p
                className="font-sans text-sm font-medium mb-2"
                style={{ color: 'oklch(0.90 0.010 50)' }}
              >
                {s.sub}
              </p>
              <p className="font-sans text-xs" style={{ color: 'oklch(0.55 0.025 45)' }}>
                {s.note}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Sector Section Generic ───────────────────────────────────────────────────
interface SectorSectionProps {
  id: string;
  label: string;
  title: string;
  subtitle: string;
  description: string;
  insight: string;
  stats: { value: string; label: string; note: string }[];
  images: string[];
  imageAlts: string[];
  imageLeft?: boolean;
  dark?: boolean;
  ctaLabel?: string;
}

function SectorSection({
  id, label, title, subtitle, description, insight, stats,
  images, imageAlts, imageLeft = false, dark = false, ctaLabel = 'Agenda diagnóstico',
}: SectorSectionProps) {
  const bg = dark ? 'oklch(0.13 0.016 38)' : 'oklch(0.97 0.008 50)';
  const altBg = dark ? 'oklch(0.16 0.020 38)' : 'oklch(0.95 0.008 46)';
  const textColor = dark ? 'oklch(0.90 0.010 50)' : 'oklch(0.18 0.012 40)';
  const mutedColor = dark ? 'oklch(0.58 0.020 45)' : 'oklch(0.48 0.020 42)';
  const accentColor = dark ? 'oklch(0.72 0.095 62)' : 'oklch(0.55 0.045 40)';

  const textCol = (
    <motion.div
      initial={{ opacity: 0, x: imageLeft ? 40 : -40 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={springPresets.smooth}
      className="flex flex-col justify-center py-12 md:py-0 px-0 md:px-12"
    >
      <p className="text-xs tracking-[0.35em] uppercase font-sans mb-4" style={{ color: mutedColor }}>
        {label}
      </p>
      <h2 className="font-heading text-4xl md:text-5xl mb-3 leading-tight" style={{ fontWeight: 300, color: textColor }}>
        {title}
      </h2>
      <p className="font-sans text-base mb-8" style={{ color: mutedColor, fontStyle: 'italic' }}>
        {subtitle}
      </p>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {stats.map((s) => (
          <div key={s.label} className="text-center p-3" style={{ background: dark ? 'oklch(0.18 0.022 38)' : 'oklch(0.97 0.008 50)' }}>
            <p className="font-heading text-3xl mb-1" style={{ fontWeight: 300, color: accentColor }}>{s.value}</p>
            <p className="font-sans text-xs font-medium mb-0.5" style={{ color: textColor }}>{s.label}</p>
            <p className="font-sans text-xs" style={{ color: mutedColor }}>{s.note}</p>
          </div>
        ))}
      </div>

      <p className="font-sans text-sm leading-relaxed mb-6" style={{ color: mutedColor }}>
        {description}
      </p>

      <div className="p-5 mb-8" style={{
        background: dark ? 'oklch(0.18 0.022 38)' : 'oklch(0.92 0.010 46)',
        borderLeft: `2px solid ${accentColor}`,
      }}>
        <p className="font-sans text-xs tracking-widest uppercase mb-2" style={{ color: mutedColor }}>
          Dato clave
        </p>
        <p className="font-sans text-sm leading-relaxed italic" style={{ color: dark ? 'oklch(0.80 0.015 48)' : 'oklch(0.35 0.020 40)' }}>
          "{insight}"
        </p>
      </div>

      <button
        onClick={() => scrollToSection('contacto')}
        className="self-start inline-flex items-center gap-2 px-8 py-3 text-xs tracking-[0.2em] uppercase font-sans transition-all hover:opacity-70"
        style={{
          background: accentColor,
          color: dark ? 'oklch(0.12 0.015 40)' : 'oklch(0.98 0.005 50)',
        }}
      >
        {ctaLabel}
        <ChevronRight size={14} strokeWidth={1.5} />
      </button>
    </motion.div>
  );

  const imgCol = (
    <motion.div
      initial={{ opacity: 0, x: imageLeft ? -40 : 40 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={springPresets.smooth}
      className={`grid gap-3 ${images.length === 2 ? 'grid-rows-2' : ''}`}
    >
      {images.map((src, idx) => (
        <div
          key={src}
          className="overflow-hidden"
          style={{ aspectRatio: images.length === 2 ? (idx === 0 ? '16/9' : '16/7') : '4/3' }}
        >
          <img
            src={src}
            alt={imageAlts[idx] ?? ''}
            className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700"
            style={IMG_FILTER}
          />
        </div>
      ))}
    </motion.div>
  );

  return (
    <section id={id} className="py-24 px-6" style={{ background: bg }}>
      <div className="container mx-auto max-w-6xl">
        <div className={`grid md:grid-cols-2 gap-12 items-center`}>
          {imageLeft ? <>{imgCol}{textCol}</> : <>{textCol}{imgCol}</>}
        </div>
      </div>
    </section>
  );
}

// ─── Antes / Después ──────────────────────────────────────────────────────────
function AntesDepues() {
  return (
    <section id="transformacion" className="py-24 px-6" style={{ background: 'oklch(0.10 0.015 38)' }}>
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={springPresets.smooth}
          className="text-center mb-16"
        >
          <SectionLabel>Transformación</SectionLabel>
          <h2
            className="font-heading text-4xl md:text-6xl leading-tight mb-4"
            style={{ fontWeight: 300, color: 'oklch(0.92 0.010 50)' }}
          >
            El mismo espacio.
            <br />
            <span style={{ fontStyle: 'italic', color: 'oklch(0.68 0.080 60)' }}>
              El doble de ingresos.
            </span>
          </h2>
          <p className="font-sans text-base max-w-xl mx-auto" style={{ color: 'oklch(0.58 0.020 45)' }}>
            No reemplazamos tu negocio. Rediseñamos la experiencia que viven tus clientes — y eso cambia todo.
          </p>
        </motion.div>

        {/* Split */}
        <div className="grid md:grid-cols-2 gap-px bg-muted/20 mb-16">
          {[
            {
              label: 'Antes',
              img: '/images/mismo_antes_2.png',
              items: ['Iluminación fluorescente fría', 'Sin flujo de Chi — energía bloqueada', 'Acústica agresiva (+80 dB)', 'Clientes incómodos. Menos tiempo. Menos consumo.'],
              accent: 'oklch(0.58 0.120 20)',
            },
            {
              label: 'Después',
              img: '/images/mismo_despues_2.png',
              items: ['Iluminación cálida en 3 zonas', 'Flujo abierto — entrada libre de bloqueos', 'Acústica óptima (65–72 dB)', 'Clientes en estado de bienestar. Más tiempo. Más consumo.'],
              accent: 'oklch(0.55 0.045 40)',
            },
          ].map(({ label, img, items, accent }) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              style={{ background: 'oklch(0.15 0.018 38)' }}
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={img} alt={label} className="w-full h-full object-cover" style={IMG_FILTER} />
              </div>
              <div className="p-8">
                <p
                  className="font-sans text-xs tracking-widest uppercase mb-4"
                  style={{ color: accent }}
                >
                  {label}
                </p>
                <ul className="space-y-3">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
                      <span className="font-sans text-sm" style={{ color: 'oklch(0.72 0.015 48)' }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* 3 resultado numbers */}
        <div className="grid grid-cols-3 gap-px bg-muted/10">
          {[
            { val: '+18%', label: 'ticket promedio', sub: 'restaurantes en Polanco' },
            { val: '×2.5', label: 'tarifa por noche', sub: 'Airbnbs en Roma Norte' },
            { val: '-30%', label: 'costo de adquisición', sub: 'hoteles boutique CDMX' },
          ].map((n) => (
            <div
              key={n.label}
              className="text-center py-10 px-6"
              style={{ background: 'oklch(0.13 0.016 38)' }}
            >
              <p
                className="font-heading text-5xl md:text-6xl mb-2"
                style={{ fontWeight: 300, color: 'oklch(0.68 0.080 60)' }}
              >
                {n.val}
              </p>
              <p className="font-sans text-sm font-medium mb-1" style={{ color: 'oklch(0.88 0.010 50)' }}>
                {n.label}
              </p>
              <p className="font-sans text-xs" style={{ color: 'oklch(0.50 0.020 44)' }}>
                {n.sub}
              </p>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <button
            onClick={() => scrollToSection('contacto')}
            className="inline-flex items-center gap-2 px-12 py-4 text-xs tracking-[0.2em] uppercase font-sans transition-all hover:opacity-80"
            style={{ background: 'oklch(0.92 0.010 50)', color: 'oklch(0.18 0.012 40)' }}
          >
            Quiero esta transformación
            <ArrowRight size={14} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── Before / After — 5 tipos de espacio ────────────────────────────────────
const SPACE_CASES = [
  {
    type: 'Airbnb · Renta Vacacional',
    headline: 'De $70 a $180 USD por noche',
    insight: 'El viajero internacional paga el doble cuando el espacio cuenta una historia. Un bedroom en Roma Norte bien diseñado genera hasta $1,800 MXN extras por noche.',
    before: '/images/case_airbnb_antes_2.png',
    after:  '/images/case_airbnb_despues_2.png',
    stat: '+157%', statLabel: 'tarifa promedio vs. cuarto sin diseño',
  },
  {
    type: 'Restaurante · Bar · Café',
    headline: '+18% en ticket promedio',
    insight: 'El 68.5% de los comensales elige un restaurante por el ambiente. La iluminación cálida, la acústica controlada y el flujo Feng Shui alargan la estadía y multiplican los pedidos de sobremesa.',
    before: '/images/case_restaurante_antes_2.png',
    after:  '/images/case_restaurante_despues_2.png',
    stat: '+18%', statLabel: 'ticket promedio en restaurantes de autor',
  },
  {
    type: 'Lobby de Hotel Boutique',
    headline: 'El lobby que convierte en reseñas de 5 estrellas',
    insight: 'El 73% de los huéspedes decide si regresa en los primeros 90 segundos dentro del hotel. Un lobby con diseño sensorial aumenta la tarifa percibida antes de que el huésped llegue a su habitación.',
    before: '/images/case_hotel_antes_2.png',
    after:  '/images/case_hotel_despues_2.png',
    stat: '+41%', statLabel: 'tarifa premium hoteles boutique vs. cadena',
  },
  {
    type: 'Cafetería · Coffee Shop',
    headline: 'De punto de paso a destino',
    insight: 'Un diseño sensorial aumenta la estadía promedio de 12 a 35 minutos. Más tiempo en sala = más consumo por visita. La iluminación Edison, las plantas y la acústica correcta crean el efecto "quiero quedarme más".',
    before: '/images/case_cafeteria_antes_2.png',
    after:  '/images/case_cafeteria_despues_2.png',
    stat: '×2.9', statLabel: 'tiempo promedio en sala',
  },
  {
    type: 'Tienda de Ropa · Boutique',
    headline: 'Visual merchandising que cierra ventas',
    insight: 'La iluminación focal, el flujo de circulación y la densidad correcta de ropa en rack aumentan la tasa de conversión hasta un 30%. El cliente no siente que le venden — siente que descubre.',
    before: '/images/case_tienda_antes_2.png',
    after:  '/images/case_tienda_despues_2.png',
    stat: '+30%', statLabel: 'tasa de conversión en boutiques',
  },
];

function BeforeAfterCases() {
  return (
    <section id="casos" className="py-24 px-6 bg-background">
      <div className="container mx-auto max-w-6xl">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={springPresets.smooth}
          className="text-center mb-20"
        >
          <SectionLabel>Transformaciones</SectionLabel>
          <h2
            className="font-heading text-4xl md:text-6xl leading-tight mb-4 text-foreground"
            style={{ fontWeight: 300 }}
          >
            El antes y el después.
            <br />
            <span style={{ fontStyle: 'italic', color: 'oklch(0.45 0.030 50)' }}>
              En cada tipo de negocio.
            </span>
          </h2>
          <p className="font-sans text-base max-w-xl mx-auto" style={{ color: 'oklch(0.50 0.020 45)' }}>
            No importa tu giro. El principio es el mismo: un espacio diseñado con intención
            convierte más, retiene más y cobra más.
          </p>
        </motion.div>

        {/* Cases */}
        <div className="space-y-20">
          {SPACE_CASES.map((c, i) => (
            <motion.div
              key={c.type}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springPresets.gentle, delay: 0.05 }}
              className={`grid md:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? 'md:grid-flow-col-dense' : ''}`}
            >
              {/* Images split */}
              <div className={`relative overflow-hidden ${i % 2 === 1 ? 'md:order-2' : ''}`}>
                {/* Before / After split */}
                <div className="flex h-72 md:h-96 overflow-hidden" style={{ gap: '2px', background: 'oklch(0.85 0.010 50)' }}>
                  {/* Antes */}
                  <div className="relative flex-1 overflow-hidden group/antes">
                    <img
                      src={c.before}
                      alt={`${c.type} antes del diseño sensorial`}
                      className="w-full h-full object-cover"
                      style={{ ...IMG_FILTER, filter: 'sepia(8%) contrast(90%) saturate(0.85)' }}
                    />
                    <div
                      className="absolute inset-0"
                      style={{ background: 'oklch(0.15 0.012 38 / 0.30)' }}
                    />
                    <span
                      className="absolute bottom-4 left-4 font-sans text-xs tracking-widest uppercase px-3 py-1"
                      style={{
                        background: 'oklch(0.08 0.010 38 / 0.80)',
                        color: 'oklch(0.68 0.020 50)',
                        border: '1px solid oklch(0.40 0.015 45 / 0.5)',
                      }}
                    >
                      Antes
                    </span>
                  </div>
                  {/* Después */}
                  <div className="relative flex-1 overflow-hidden">
                    <img
                      src={c.after}
                      alt={`${c.type} después del diseño sensorial Etérico Studio`}
                      className="w-full h-full object-cover"
                      style={IMG_FILTER}
                    />
                    <span
                      className="absolute bottom-4 right-4 font-sans text-xs tracking-widest uppercase px-3 py-1"
                      style={{
                        background: 'oklch(0.62 0.065 55 / 0.90)',
                        color: 'oklch(0.98 0.005 50)',
                      }}
                    >
                      Después
                    </span>
                  </div>
                </div>
              </div>

              {/* Text */}
              <div className={`space-y-5 ${i % 2 === 1 ? 'md:order-1' : ''}`}>
                <p
                  className="font-sans text-xs tracking-widest uppercase"
                  style={{ color: 'oklch(0.55 0.030 50)' }}
                >
                  {c.type}
                </p>
                <h3
                  className="font-heading text-2xl md:text-3xl text-foreground leading-snug"
                  style={{ fontWeight: 300 }}
                >
                  {c.headline}
                </h3>
                <p
                  className="font-sans text-sm leading-relaxed"
                  style={{ color: 'oklch(0.50 0.020 45)' }}
                >
                  {c.insight}
                </p>
                {/* Stat */}
                <div
                  className="inline-flex items-baseline gap-3 pt-2"
                  style={{ borderTop: '1px solid oklch(0.85 0.010 50)' }}
                >
                  <span
                    className="font-heading text-4xl"
                    style={{ fontWeight: 300, color: 'oklch(0.38 0.025 45)' }}
                  >
                    {c.stat}
                  </span>
                  <span
                    className="font-sans text-xs leading-tight max-w-[160px]"
                    style={{ color: 'oklch(0.55 0.020 45)' }}
                  >
                    {c.statLabel}
                  </span>
                </div>
                <div className="pt-1">
                  <button
                    onClick={() => scrollToSection('contacto')}
                    className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.15em] uppercase transition-all hover:opacity-70"
                    style={{ color: 'oklch(0.38 0.025 45)' }}
                  >
                    Quiero este resultado
                    <ArrowRight size={12} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ ...springPresets.smooth, delay: 0.1 }}
          className="text-center mt-20 pt-12"
          style={{ borderTop: '1px solid oklch(0.88 0.010 50)' }}
        >
          <p
            className="font-heading text-xl md:text-2xl mb-6"
            style={{ fontWeight: 300, color: 'oklch(0.38 0.025 45)', fontStyle: 'italic' }}
          >
            ¿Cuál es tu espacio?
          </p>
          <button
            onClick={() => scrollToSection('contacto')}
            className="inline-flex items-center justify-center gap-2 px-12 py-4 text-xs tracking-[0.2em] uppercase font-sans transition-all hover:opacity-80"
            style={{ background: 'oklch(0.28 0.018 40)', color: 'oklch(0.92 0.010 50)' }}
          >
            Diagnóstico gratuito
            <ArrowRight size={14} strokeWidth={1.5} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

// ─── CDMX 2026 ────────────────────────────────────────────────────────────────
function CDMX2026() {
  return (
    <section className="relative py-28 px-6 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: 'url(/images/cdmx_terraza_2.png)' }}
      />
      <div
        className="absolute inset-0"
        style={{ background: 'oklch(0.08 0.010 38 / 0.82)' }}
      />
      <div className="relative z-10 container mx-auto max-w-5xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={springPresets.smooth}
        >
          <SectionLabel>Ciudad de México</SectionLabel>
          <h2
            className="font-heading text-4xl md:text-6xl text-white leading-tight mb-8"
            style={{ fontWeight: 300 }}
          >
            CDMX está en su mejor momento.
            <br />
            <span style={{ fontStyle: 'italic', color: 'oklch(0.72 0.095 62)' }}>
              ¿Tu espacio también?
            </span>
          </h2>
          <p
            className="font-sans text-base max-w-2xl mx-auto mb-16 leading-relaxed"
            style={{ color: 'oklch(0.80 0.012 50)', fontWeight: 300 }}
          >
            Ciudad de México atrae hoy más turismo internacional que nunca.
            El viajero que llega busca autenticidad local con diseño de nivel global.
            Los negocios con espacios diseñados capturan esa demanda — los demás la dejan pasar.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
            {[
              { icon: Star,       val: '#1 destino',     sub: 'turismo urbano Latinoamérica' },
              { icon: DollarSign, val: '$3.2T USD',       sub: 'mercado lujo global 2033' },
              { icon: Users,      val: '40%',             sub: 'gasto turístico en el barrio' },
              { icon: MapPin,     val: 'Roma · Polanco',  sub: 'zonas de mayor retorno' },
            ].map(({ icon: Icon, val, sub }) => (
              <div
                key={val}
                className="py-8 px-4 text-center"
                style={{ background: 'oklch(0.12 0.015 38 / 0.7)' }}
              >
                <Icon size={20} strokeWidth={1} className="mx-auto mb-3" style={{ color: 'oklch(0.68 0.080 60)' }} />
                <p
                  className="font-heading text-2xl md:text-3xl mb-1 text-white"
                  style={{ fontWeight: 300 }}
                >
                  {val}
                </p>
                <p className="font-sans text-xs" style={{ color: 'oklch(0.60 0.020 45)' }}>
                  {sub}
                </p>
              </div>
            ))}
          </div>

          <p
            className="mt-12 font-sans text-sm tracking-widest uppercase"
            style={{ color: 'oklch(0.68 0.080 60)' }}
          >
            El momento de rediseñar tu espacio es ahora — antes que tu competencia.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Case Studies ─────────────────────────────────────────────────────────────
const BADGE_STYLE: Record<string, { bg: string; color: string }> = {
  EXITOSO: { bg: 'oklch(0.55 0.045 40 / 0.15)', color: 'oklch(0.55 0.045 40)' },
  MEJORAS: { bg: 'oklch(0.68 0.080 60 / 0.15)', color: 'oklch(0.60 0.060 50)' },
  ERRORES: { bg: 'oklch(0.58 0.120 20 / 0.15)', color: 'oklch(0.58 0.120 20)' },
};

function CaseStudies() {
  const [active, setActive] = useState(0);
  const cur = CASE_STUDIES[active];

  return (
    <section id="casos" className="py-28 px-6 bg-background">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={springPresets.smooth}
          className="text-center mb-16"
        >
          <SectionLabel>Evidencia Global</SectionLabel>
          <h2
            className="font-heading text-4xl md:text-6xl text-foreground leading-tight"
            style={{ fontWeight: 300 }}
          >
            El Feng Shui que
            <br />
            <span style={{ fontStyle: 'italic' }}>construyó imperios</span>
          </h2>
        </motion.div>

        {/* Selector tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {CASE_STUDIES.map((c, i) => (
            <button
              key={c.id}
              onClick={() => setActive(i)}
              className="px-4 py-2 text-xs tracking-widest uppercase font-sans transition-all duration-200"
              style={{
                background: active === i ? 'oklch(0.18 0.012 40)' : 'oklch(0.95 0.008 46)',
                color: active === i ? 'oklch(0.97 0.008 50)' : 'oklch(0.48 0.020 42)',
              }}
            >
              {c.id.toUpperCase()}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={springPresets.gentle}
            className="grid md:grid-cols-2"
          >
            <div className="relative overflow-hidden" style={{ aspectRatio: '4/3' }}>
              <img src={cur.image} alt={cur.title} className="w-full h-full object-cover" style={IMG_FILTER} />
              <div
                className="absolute top-4 left-4 px-3 py-1 text-xs tracking-widest uppercase font-sans"
                style={{ background: 'oklch(0.10 0.015 38 / 0.88)', color: 'oklch(0.85 0.015 50)' }}
              >
                {cur.sector}
              </div>
              <div
                className="absolute bottom-4 left-4 px-3 py-1 text-xs tracking-widest uppercase font-sans"
                style={{
                  background: BADGE_STYLE[cur.badge]?.bg,
                  color: BADGE_STYLE[cur.badge]?.color,
                  border: `1px solid ${BADGE_STYLE[cur.badge]?.color}`,
                }}
              >
                {cur.badge}
              </div>
            </div>

            <div className="p-10 flex flex-col justify-between" style={{ background: 'oklch(0.96 0.008 48)' }}>
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-muted-foreground mb-2">{cur.location}</p>
                <h3 className="font-heading text-3xl text-foreground mb-5" style={{ fontWeight: 300 }}>{cur.title}</h3>
                <div className="flex items-baseline gap-3 mb-5">
                  <span className="font-heading text-5xl" style={{ fontWeight: 300, color: 'oklch(0.55 0.045 40)' }}>{cur.metric}</span>
                  <span className="font-sans text-sm text-muted-foreground max-w-[180px] leading-tight">{cur.metricLabel}</span>
                </div>
                <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-5">{cur.description}</p>
                <InsightBox>{cur.insight}</InsightBox>
              </div>
              <div className="flex gap-2 mt-8">
                {CASE_STUDIES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    className="transition-all duration-300"
                    style={{
                      width: active === i ? '24px' : '8px',
                      height: '2px',
                      background: active === i ? 'oklch(0.55 0.045 40)' : 'oklch(0.80 0.012 45)',
                    }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Compact thumbnail strip */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-px bg-border mt-px">
          {CASE_STUDIES.map((c, i) => (
            <motion.button
              key={c.id}
              onClick={() => setActive(i)}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="bg-background text-left p-3 hover:bg-muted/40 transition-colors duration-200"
              style={{ background: active === i ? 'oklch(0.93 0.010 46)' : undefined }}
            >
              <div className="aspect-[4/3] overflow-hidden mb-2">
                <img src={c.image} alt={c.title} className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity" style={IMG_FILTER} />
              </div>
              <p className="font-sans text-xs leading-tight text-foreground truncate" style={{ fontWeight: 500 }}>
                {c.title.split(' ').slice(0, 2).join(' ')}
              </p>
              <p className="font-sans text-xs text-muted-foreground mt-0.5 truncate">{c.location.split('·')[0].trim()}</p>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Process Steps ────────────────────────────────────────────────────────────
function Process() {
  return (
    <section id="proceso" className="py-28 px-6" style={{ background: 'oklch(0.95 0.008 46)' }}>
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={springPresets.smooth}
          className="text-center mb-20"
        >
          <SectionLabel>Nuestro Proceso</SectionLabel>
          <h2
            className="font-heading text-4xl md:text-5xl text-foreground leading-tight"
            style={{ fontWeight: 300 }}
          >
            De la primera visita
            <br />
            <span style={{ fontStyle: 'italic' }}>a resultados medibles</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-4 gap-px bg-border">
          {PROCESS_STEPS.map((step, i) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springPresets.gentle, delay: i * 0.12 }}
              className="bg-background p-8 group hover:bg-muted/20 transition-colors duration-400"
            >
              <p
                className="font-heading text-7xl mb-6"
                style={{ fontWeight: 300, color: 'oklch(0.88 0.012 45)' }}
              >
                {step.num}
              </p>
              <h3 className="font-heading text-xl text-foreground mb-3" style={{ fontWeight: 400 }}>
                {step.title}
              </h3>
              <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-14"
        >
          <button
            onClick={() => scrollToSection('contacto')}
            className="inline-flex items-center gap-2 px-12 py-4 text-xs tracking-[0.2em] uppercase font-sans transition-all hover:opacity-75"
            style={{ background: 'oklch(0.18 0.012 40)', color: 'white' }}
          >
            Agenda tu diagnóstico gratuito
            <ArrowRight size={14} strokeWidth={1.5} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Viviana Pacheco ──────────────────────────────────────────────────────────
function Viviana() {
  return (
    <section id="viviana" className="py-28 px-6" style={{ background: 'oklch(0.12 0.015 40)' }}>
      <div className="container mx-auto max-w-5xl">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={springPresets.smooth}
          >
            <p className="text-xs tracking-[0.35em] uppercase font-sans mb-5" style={{ color: 'oklch(0.55 0.025 45)' }}>
              La Especialista
            </p>
            <h2
              className="font-heading text-4xl md:text-5xl mb-6 leading-tight"
              style={{ fontWeight: 300, color: 'oklch(0.92 0.010 50)', fontStyle: 'italic' }}
            >
              Viviana Pacheco
            </h2>
            <p className="font-sans text-xs tracking-widest uppercase mb-8" style={{ color: 'oklch(0.55 0.025 45)' }}>
              Arquitecta · Especialista en Hospitalidad Sensorial · CDMX
            </p>
            <p className="font-sans text-base leading-relaxed mb-5" style={{ color: 'oklch(0.72 0.015 48)' }}>
              Arquitecta especializada en diseño sensorial para negocios de hospitalidad.
              Viviana lleva más de una década convirtiendo restaurantes, Airbnbs y hoteles
              boutique en CDMX en máquinas de generar reseñas de 5 estrellas y tarifas premium.
            </p>
            <p className="font-sans text-base leading-relaxed mb-10" style={{ color: 'oklch(0.72 0.015 48)' }}>
              Su método integra neuroarquitectura, ergonomía aplicada y principios de Feng Shui
              con la identidad visual y cultural de la Ciudad de México — porque el mejor diseño
              para CDMX solo lo puede hacer alguien que la vive.
            </p>
            <div className="space-y-3">
              {[
                'Especialista en hospitalidad sensorial para CDMX',
                'Neuroarquitectura y diseño Feng Shui aplicado',
                'Ergonomía contemplada en el 100% de los proyectos',
                'ROI medible a 90 días en cada proyecto comercial',
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <span
                    className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full"
                    style={{ background: 'oklch(0.68 0.080 60)' }}
                  />
                  <p className="font-sans text-sm leading-relaxed" style={{ color: 'oklch(0.60 0.020 45)' }}>
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={springPresets.smooth}
            className="relative"
          >
            <div className="aspect-[3/4] overflow-hidden">
              <img
                src="/images/hero_luz_2.png"
                alt="Etérico Studio — diseño sensorial CDMX"
                className="w-full h-full object-cover"
                style={{ filter: 'sepia(8%) contrast(92%)' }}
              />
            </div>
            <div
              className="absolute bottom-6 right-6 p-4"
              style={{ background: 'oklch(0.12 0.015 40 / 0.90)', backdropFilter: 'blur(8px)' }}
            >
              <img
                src={IMAGES.WHATSAPP_IMAGE_2026_04_23_AT_21_58_42_REMOVEBG_PREVIEW_1}
                alt="Etérico Studio"
                className="h-7 w-auto"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── Gallery ──────────────────────────────────────────────────────────────────
function Gallery() {
  return (
    <section id="galeria" className="py-24 px-6 bg-background">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={springPresets.smooth}
          className="text-center mb-12"
        >
          <SectionLabel>Proyectos</SectionLabel>
          <h2 className="font-heading text-4xl md:text-5xl text-foreground leading-tight" style={{ fontWeight: 300 }}>
            Espacios que{' '}
            <span style={{ fontStyle: 'italic' }}>generan dinero</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {GALLERY_IMAGES.map((img, i) => (
            <motion.div
              key={img.url}
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ ...springPresets.gentle, delay: i * 0.07 }}
              className="overflow-hidden group"
              style={{ aspectRatio: i === 3 ? '3/4' : '4/3' }}
            >
              <img
                src={img.url}
                alt={img.alt}
                className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                style={IMG_FILTER}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Contact ──────────────────────────────────────────────────────────────────
function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', type: '', message: '' });
  const [sent, setSent] = useState(false);

  return (
    <section id="contacto" className="py-28 px-6" style={{ background: 'oklch(0.97 0.008 50)' }}>
      <div className="container mx-auto max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={springPresets.smooth}
          className="text-center mb-16"
        >
          <SectionLabel>Diagnóstico Gratuito</SectionLabel>
          <h2
            className="font-heading text-4xl md:text-6xl text-foreground leading-tight mb-4"
            style={{ fontWeight: 300 }}
          >
            ¿Cuánto dinero está dejando
            <br />
            <span style={{ fontStyle: 'italic' }}>sobre la mesa tu espacio?</span>
          </h2>
          <p className="font-sans text-base text-muted-foreground max-w-xl mx-auto">
            La primera consulta es gratuita. Te decimos exactamente cuánto puedes
            ganar con una intervención de diseño en tu negocio específico.
          </p>
        </motion.div>

        {!sent ? (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            onSubmit={(e) => { e.preventDefault(); setSent(true); }}
            className="grid md:grid-cols-2 gap-6"
          >
            {[
              { key: 'name',  label: 'Nombre', type: 'text',  ph: 'Tu nombre', required: true },
              { key: 'email', label: 'Email',  type: 'email', ph: 'tu@correo.com', required: true },
              { key: 'phone', label: 'WhatsApp', type: 'tel', ph: '+52 55 0000 0000', required: false },
            ].map(({ key, label, type, ph, required }) => (
              <div key={key}>
                <label className="block text-xs tracking-widest uppercase font-sans text-muted-foreground mb-2">
                  {label}
                </label>
                <input
                  type={type}
                  required={required}
                  value={form[key as keyof typeof form]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  placeholder={ph}
                  className="w-full px-4 py-3 bg-transparent font-sans text-sm text-foreground focus:outline-none"
                  style={{ borderBottom: '1px solid oklch(0.88 0.012 45)' }}
                />
              </div>
            ))}

            <div>
              <label className="block text-xs tracking-widest uppercase font-sans text-muted-foreground mb-2">
                Tipo de espacio
              </label>
              <select
                required
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full px-4 py-3 bg-transparent font-sans text-sm text-foreground focus:outline-none cursor-pointer"
                style={{ borderBottom: '1px solid oklch(0.88 0.012 45)' }}
              >
                <option value="">Selecciona</option>
                <option value="restaurante">Restaurante / Bar</option>
                <option value="airbnb">Airbnb / Renta vacacional</option>
                <option value="hotel">Hotel boutique</option>
                <option value="oficina">Oficina / Coworking</option>
                <option value="hogar">Hogar residencial</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs tracking-widest uppercase font-sans text-muted-foreground mb-2">
                Cuéntanos brevemente tu proyecto
              </label>
              <textarea
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="¿Qué tipo de negocio tienes? ¿Cuál es tu principal reto?"
                className="w-full px-4 py-3 bg-transparent font-sans text-sm text-foreground focus:outline-none resize-none"
                style={{ borderBottom: '1px solid oklch(0.88 0.012 45)' }}
              />
            </div>

            <div className="md:col-span-2 flex flex-col sm:flex-row gap-4 items-center">
              <button
                type="submit"
                className="px-12 py-4 text-xs tracking-[0.2em] uppercase font-sans transition-all hover:opacity-80"
                style={{ background: 'oklch(0.18 0.012 40)', color: 'white' }}
              >
                Solicitar diagnóstico gratuito
              </button>
              <a
                href="https://wa.me/525500000000"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 text-xs tracking-[0.2em] uppercase font-sans transition-all hover:opacity-60"
                style={{ border: '1px solid oklch(0.88 0.012 45)', color: 'oklch(0.48 0.020 42)' }}
              >
                <Phone size={13} strokeWidth={1.5} />
                WhatsApp directo
              </a>
            </div>
          </motion.form>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={springPresets.bouncy}
            className="text-center py-20"
          >
            <div
              className="w-14 h-14 mx-auto flex items-center justify-center mb-8 font-heading text-2xl"
              style={{ background: 'oklch(0.92 0.010 46)', color: 'oklch(0.55 0.045 40)' }}
            >
              ✓
            </div>
            <h3 className="font-heading text-3xl text-foreground mb-3" style={{ fontWeight: 300, fontStyle: 'italic' }}>
              Gracias, {form.name}.
            </h3>
            <p className="font-sans text-base text-muted-foreground">
              Viviana revisará tu mensaje en menos de 24 horas para coordinar tu diagnóstico.
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="py-14 px-6" style={{ background: 'oklch(0.10 0.015 38)', borderTop: '1px solid oklch(0.20 0.018 40)' }}>
      <div className="container mx-auto max-w-6xl flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="text-center md:text-left">
          <img
            src={IMAGES.WHATSAPP_IMAGE_2026_04_23_AT_21_58_42_REMOVEBG_PREVIEW_1}
            alt="Etérico Studio"
            className="h-9 w-auto mb-3 mx-auto md:mx-0"
          />
          <p className="font-sans text-xs tracking-widest uppercase" style={{ color: 'oklch(0.40 0.018 42)' }}>
            Diseño Sensorial · CDMX
          </p>
          <p className="font-sans text-xs mt-1" style={{ color: 'oklch(0.30 0.015 40)' }}>
            Arq. Viviana Pacheco
          </p>
        </div>

        <div className="flex items-center gap-6">
          {[Instagram, Mail, Phone].map((Icon, i) => (
            <a key={i} href="#" className="transition-opacity hover:opacity-50" style={{ color: 'oklch(0.40 0.018 42)' }}>
              <Icon size={18} strokeWidth={1} />
            </a>
          ))}
        </div>
      </div>

      <div className="mt-10 pt-6 container mx-auto max-w-6xl flex flex-col md:flex-row items-center justify-between gap-3" style={{ borderTop: '1px solid oklch(0.18 0.015 38)' }}>
        <p className="font-sans text-xs" style={{ color: 'oklch(0.30 0.015 40)' }}>
          © 2026 Etérico Studio · Todos los derechos reservados
        </p>
        <p className="font-sans text-xs" style={{ color: 'oklch(0.30 0.015 40)' }}>
          Diseño que se mide · Resultados que se sienten
        </p>
      </div>
    </footer>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Home() {
  return (
    <main className="w-full">
      <Navbar />
      <Hero />
      <MacroStats />

      {/* Restaurantes */}
      <SectorSection
        id="restaurantes"
        label="Restaurantes · Bar · Café"
        title="Restaurantes que se llenan solos"
        subtitle="El 68.5% de tus clientes elige por el ambiente — no por la comida"
        description="El diseño sensorial guía el comportamiento de consumo sin que el cliente lo note. La acústica correcta (65–72 dB) prolonga la estadía y aumenta los pedidos de sobremesa. La iluminación tenue en fine dining incrementa la percepción de valor y justifica precios más altos. El flujo Feng Shui elimina los puntos de fricción que hacen que el cliente se incomode o se vaya antes de la cuenta."
        insight="En Polanco, los restaurantes con diseño sensorial reportan tickets promedio superiores a $1,500 MXN. La misma comida. El mismo chef. Solo el espacio cambia."
        stats={RESTAURANTE_STATS}
        images={['/images/cdmx_restaurante_2.png']}
        imageAlts={['Restaurante sensorial Polanco CDMX']}
        imageLeft={false}
        ctaLabel="Quiero más reservas"
      />

      {/* Airbnb */}
      <SectorSection
        id="airbnb"
        label="Airbnb · Rentas Vacacionales"
        title="Airbnbs que cobran el triple"
        subtitle="Mismo departamento en Roma Norte. El diseño decide si ganas $70 o $180 USD por noche."
        description="En las plataformas digitales, el diseño es el primer filtro de conversión. Un Airbnb con diseño editorial atrae al huésped internacional que busca una experiencia — no solo una cama. Las fotografías profesionales de un espacio bien diseñado pueden duplicar los ingresos mensuales al posicionarte en los primeros resultados del algoritmo de Airbnb. La inversión en un upgrade de diseño ($5,000–$10,000 USD) se recupera en menos de 12 meses."
        insight="Roma Norte y Condesa: el diseño de autor genera ocupación permanente incluso en temporada baja. La Juárez emerge como la zona de mayor crecimiento en plusvalía por diseño."
        stats={AIRBNB_STATS}
        images={['/images/cdmx_airbnb_2.png', '/images/cdmx_room_airbnb_2.png']}
        imageAlts={['Airbnb editorial Roma Norte', 'Cuarto Airbnb lujoso mexicano']}
        imageLeft={true}
        dark={false}
        ctaLabel="Quiero subir mis tarifas"
      />

      {/* Hoteles */}
      <SectorSection
        id="hoteles"
        label="Hoteles Boutique · Posadas"
        title="El hotel que se viraliza solo"
        subtitle="El efecto 'Instagrammable' no es casualidad. Es una decisión de diseño."
        description="Los hoteles boutique con diseño sensorial superan a las cadenas en RevPAR con crecimientos del 6% anual. Más importante: el contenido generado por los huéspedes en redes sociales actúa como marketing masivo gratuito, reduciendo el costo de adquisición de cliente en hasta un 30%. CDMX recibe hoy un flujo histórico de turismo internacional — y el viajero que llega busca diseño con alma mexicana. Eso se paga."
        insight="El turista internacional que elige CDMX busca identidad cultural con nivel global. Un espacio con diseño sensorial se convierte en destino por sí mismo — y se llena antes de cualquier temporada."
        stats={HOTEL_STATS}
        images={['/images/cdmx_hotel_2.png', '/images/cdmx_terraza_2.png']}
        imageAlts={['Lobby hotel boutique CDMX', 'Terraza rooftop con vista CDMX']}
        imageLeft={false}
        dark={false}
        ctaLabel="Quiero más ocupación"
      />

      <AntesDepues />
      <BeforeAfterCases />
      <CDMX2026 />
      <CaseStudies />
      <Process />
      <Viviana />
      <Contact />
      <Footer />
    </main>
  );
}
